import type { RootState } from '@/redux/RootReducer';
import { Divider, Form } from 'antd';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getPostalSearchProgress, getPostalSearchReset } from '../../../../redux/common/CommonActions';
import { routeFileSelector } from '../../../../utilz/i18n';
import Button from '../../../components/Buttons/Button';
import EmptyRecords from '../../../components/Common/EmptyRecords';
import CommonModal from '../../../components/Modals/CommonModal';
import ScrollPagination from '../../../components/Paginations/ScrollPagination';
import type { PostalInfo } from '../types';
import PostalSearchCard from './PostalSearchCard';
import PostalSearchForm, { type PostalSearchFormValues } from './PostalSearchForm';

export interface PostalSearchSelectValues {
  state: string;
  city: string;
  country: string;
  zip: string;
}

interface PostalSearchModalProps {
  open: boolean;
  handleSelect: (data: PostalInfo) => void;
  handleClose: () => void;
  countryCode?: string;
  labelForTestId?: string;
}

const PostalSearchModal: FC<PostalSearchModalProps> = ({
  open,
  handleSelect,
  handleClose,
  countryCode,
  labelForTestId,
}) => {
  const dispatch = useDispatch();
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [form] = Form.useForm<PostalSearchFormValues>();
  const { postalSearch } = useSelector((state: RootState) => state.common);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  useEffect(() => {
    if (countryCode) {
      if (Array.isArray(countryCode)) {
        form.setFieldValue('countryCode', countryCode[0]);
      } else {
        form.setFieldValue('countryCode', countryCode);
      }
    }
  }, [countryCode]);

  const onSubmit = () => {
    setIsFiltered(true);
  };

  const handleReset = () => {
    setIsFiltered(false);
    dispatch(getPostalSearchReset());
    form.resetFields();
    if (countryCode) {
      form.setFieldValue('countryCode', countryCode);
    }
  };

  const handleCloseLocal = () => {
    handleReset();
    handleClose();
  };

  const handleNextPage = () => {
    const payload = {
      ...postalSearch.requestData,
      PageIndex: postalSearch.requestData.PageIndex + 1,
    };
    dispatch(getPostalSearchProgress(payload));
  };

  return (
    <CommonModal
      title={`Postal ${t('translation:default.buttons.search')}`}
      open={open}
      className={`${isFiltered ? 'vh-85' : 'h-auto'}`}
      customFooter={
        isFiltered ? (
          <div className="pr-15 pb-15">
            <Button
              id="cancelButtonId"
              variant="danger"
              type="default"
              onClick={handleCloseLocal}
              className="float-right"
            >
              {t('translation:default.buttons.cancel')}
            </Button>
          </div>
        ) : (
          <></>
        )
      }
      descriptionClassName="mb-5 d-flex flex-direction-column"
      contentClassName="p-0"
      onCancel={handleCloseLocal}
      onClose={handleCloseLocal}
      labelForTestId={labelForTestId ? `${labelForTestId}-postal-modal` : ''}
    >
      <div className="py-15 d-flex flex-direction-column flex-1 overflow-hidden">
        <div className="px-15">
          <PostalSearchForm form={form} onSubmit={onSubmit} labelForTestId={labelForTestId} />
        </div>

        {isFiltered && <Divider className="my-15 border-top-border" />}
        <div className="flex-1 overflow-hidden">
          {isFiltered && (
            <>
              <div className="h-100 overflow-hidden ">
                <div className="py-15 h-100 overflow-hidden">
                  <div className=" h-100 overflow-hidden">
                    <ScrollPagination
                      className="h-100 px-15"
                      loadMore={handleNextPage}
                      isLoading={postalSearch?.progressing}
                      hasMore={
                        postalSearch?.progressing
                          ? false
                          : postalSearch?.requestData.PageIndex <
                            Math.ceil(postalSearch?.totalRecords / postalSearch?.requestData.PageSize)
                      }
                      scrollOffset={50}
                    >
                      {postalSearch?.data?.length ? (
                        <>
                          {postalSearch?.data.map((row) => (
                            <PostalSearchCard
                              key={row.zip}
                              data={row}
                              handleSelect={(data) => {
                                handleSelect(data);
                                handleReset();
                              }}
                            />
                          ))}
                        </>
                      ) : (
                        !postalSearch?.progressing && (postalSearch?.success || postalSearch?.error) && <EmptyRecords />
                      )}
                    </ScrollPagination>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </CommonModal>
  );
};

export default PostalSearchModal;
